import { useState } from "react";
import {
    Input,
    DatePicker,
    Radio,
    Button,
    message,
    Modal,
} from "antd";
import {
    DeleteOutlined,
    CheckOutlined,
    PlusOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import i18n from "../../../lang/i18n";
import { useTranslation } from "react-i18next";

const { TextArea } = Input;

interface Contact {
    id: number;
    title: string;
    fullName: string;
    position: string;
    phone: string;
    email: string;
    birthday: string;
    gender: string;
    notes: string;
}

export default function ContactList() {
    const { t } = useTranslation();
    const [contacts, setContacts] = useState<Contact[]>([
        {
            id: 1,
            title: "",
            fullName: "",
            position: "",
            phone: "",
            email: "",
            birthday: "",
            gender: "",
            notes: "",
        },
    ]);

    const handleChange = (
        id: number,
        field: keyof Contact,
        value: string
    ) => {
        setContacts((prev) =>
            prev.map((c) =>
                c.id === id ? { ...c, [field]: value } : c
            )
        );
    };

    const handleAddContact = () => {
        setContacts((prev) => [
            ...prev,
            {
                id: Date.now(),
                title: "",
                fullName: "",
                position: "",
                phone: "",
                email: "",
                birthday: "",
                gender: "",
                notes: "",
            },
        ]);
    };

    const handleDelete = (id: number) => {
        Modal.confirm({
            title: t('cd.delete_contact'),
            content: t('cd.delete_contact_confirm'),
            okText: t('cd.yes'),
            cancelText: t('cd.no'),
            okType: "danger",
            centered: true,
            onOk: () => {
                setContacts((prev) => prev.filter((c) => c.id !== id));
                message.success(t('cd.contact_deleted'));
            },
        });
    };

    return (
        <div className="space-y-6">
            {contacts.map((contact, index) => (
                <div
                    key={contact.id}
                    className="p-4 rounded-lg border border-gray-200
                     bg-gray-50
                     dark:bg-[#1f1f1f]
                     dark:border-[#303030]"
                >
                    {/* Header */}
                    <div className="flex justify-between items-center mb-4">
                        <span className="font-medium text-gray-800 text-000000e0">
                            {t('cd.contact')} {index + 1}
                        </span>

                        <div className="flex gap-3">
                            <DeleteOutlined
                                className="text-red-500 cursor-pointer"
                                onClick={() => handleDelete(contact.id)}
                            />
                            <CheckOutlined
                                className="text-green-500 cursor-pointer"
                                onClick={() => {
                                    message.success(t('cd.contact_saved'));
                                }}
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        <FormInput
                            label={t('cd.title_label')}
                            value={contact.title}
                            onChange={(v) =>
                                handleChange(contact.id, "title", v)
                            }
                        />

                        <FormInput
                            label={t('cd.fullname')}
                            value={contact.fullName}
                            onChange={(v) =>
                                handleChange(contact.id, "fullName", v)
                            }
                        />

                        <FormInput
                            label={t('cd.position')}
                            value={contact.position}
                            onChange={(v) =>
                                handleChange(contact.id, "position", v)
                            }
                        />

                        <FormInput
                            label={t('cd.phone_label')}
                            value={contact.phone}
                            onChange={(v) =>
                                handleChange(contact.id, "phone", v)
                            }
                        />

                        <FormInput
                            label={t('cd.email')}
                            value={contact.email}
                            onChange={(v) =>
                                handleChange(contact.id, "email", v)
                            }
                        />

                        {/* Birthday */}
                        <div className="flex items-center gap-4">
                            <label className="w-32 text-gray-700 text-000000e0">
                                {t('cd.birthday_label')}
                            </label>
                            <DatePicker
                                format={i18n.language === 'vi' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'}
                                value={
                                    contact.birthday
                                        ? dayjs(contact.birthday)
                                        : undefined
                                }
                                onChange={(date) =>
                                    handleChange(
                                        contact.id,
                                        "birthday",
                                        date ? date.format("YYYY-MM-DD") : ""
                                    )
                                }
                                className="flex-1 dark:bg-[#141414]
                           dark:border-[#303030]
                           text-000000e0"
                            />
                        </div>

                        {/* Gender */}
                        <div className="flex items-center gap-4">
                            <label className="w-32 text-gray-700 text-000000e0">
                                {t('cd.gender_label')}
                            </label>
                            <Radio.Group
                                value={contact.gender}
                                onChange={(e) =>
                                    handleChange(
                                        contact.id,
                                        "gender",
                                        e.target.value
                                    )
                                }
                                className="dark:[&_.ant-radio-wrapper]:text-gray-200"
                            >
                                <Radio value="Male">{t('cd.male')}</Radio>
                                <Radio value="Female">{t('cd.female')}</Radio>
                                <Radio value="Other">{t('cd.other_gender')}</Radio>
                            </Radio.Group>
                        </div>

                        {/* Ghi chú */}
                        <div className="flex items-start gap-4">
                            <label className="w-32 text-gray-700 text-000000e0">
                                {t('cd.note_label')}
                            </label>
                            <TextArea
                                rows={3}
                                value={contact.notes}
                                onChange={(e) =>
                                    handleChange(
                                        contact.id,
                                        "notes",
                                        e.target.value
                                    )
                                }
                                className="flex-1 dark:bg-[#141414]
                           dark:border-[#303030]
                           text-000000e0"
                            />
                        </div>
                    </div>
                </div>
            ))}

            {/* Add contact button */}
            <div
                onClick={handleAddContact}
                className="flex items-center justify-center gap-2
                   border border-dashed border-gray-300
                   rounded-lg py-3 cursor-pointer
                   hover:bg-gray-50
                   dark:border-[#303030]
                   dark:hover:bg-[#1f1f1f]"
            >
                <PlusOutlined />
                <span className="font-medium">{t('cd.add_contact')}</span>
            </div>
        </div>
    );
}

function FormInput({
    label,
    value,
    onChange,
}: {
    label: string;
    value: string;
    onChange: (v: string) => void;
}) {
    const { t } = useTranslation();

    return (
        <div className="flex items-center gap-4">
            <label className="w-32 text-gray-700 text-000000e0">
                {label}:
            </label>
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={t('cd.enter_placeholder', { label: label.toLowerCase() })}
                className="flex-1 dark:bg-[#141414]
                   dark:border-[#303030]
                   text-000000e0"
            />
        </div>
    );
}